import {
  Box, Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import CustomHeader from '../../components/General/CustomHeader';
import Page from '../../components/Layout/Page';
import { AccountSchema, GetAccountsPerPageResponse } from '../../requests/api/apiTypes';
import useFetchApi from '../../hooks/useFetchApi';
import FarmsCounter from './components/FarmsCounter';
import FarmsTableContent from './components/FarmsTableContent';
import useTableStyles from '../ClientDetail/Layout/styles';

const FarmsListView = () => {
  const classes = useTableStyles();
  const history = useHistory();
  const location = useLocation();

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      accountId: params.get('accountId') || '',
      page: parseInt(params.get('page') || '0', 10),
      rowsPerPage: parseInt(params.get('rowsPerPage') || '10', 10),
      query: params.get('query') || '',
    };
  };

  const updateQueryParams = (updates: {
    accountId?: string;
    page?: number;
    rowsPerPage?: number;
    query?: string;
  }) => {
    const params = new URLSearchParams(location.search);

    if (updates.accountId !== undefined) {
      if (updates.accountId) {
        params.set('accountId', updates.accountId);
      } else {
        params.delete('accountId');
      }
    }
    if (updates.page !== undefined) {
      params.set('page', String(updates.page));
    }
    if (updates.rowsPerPage !== undefined) {
      params.set('rowsPerPage', String(updates.rowsPerPage));
    }
    if (updates.query !== undefined) {
      if (updates.query) {
        params.set('query', updates.query);
      } else {
        params.delete('query');
      }
    }

    history.replace({ search: params.toString() });
  };

  const initialParams = getQueryParams();

  const [selectedAccountId, setSelectedAccountId] = useState<string>(initialParams.accountId);
  const [paginationParameters, setPaginationParameters] = useState({
    page: initialParams.page,
    rowsPerPage: initialParams.rowsPerPage,
  });
  const [query, setQuery] = useState<string>(initialParams.query);

  const { data: accountsData } = useFetchApi<GetAccountsPerPageResponse>({
    modelName: 'accounts',
    modelEndpoint: 'accountsList',
    inputData: { limit: 100, page: 0 },
    cacheKey: 'accounts:dropdown:limit=100',
    cacheTTLms: 5 * 60 * 1000,
  });

  const accounts = useMemo<AccountSchema[]>(() => {
    const list = accountsData?.accounts;
    if (!list) return [];
    return [...list].sort(
      (a, b) => a.name?.localeCompare(b.name || '') || 0,
    ) as unknown as AccountSchema[];
  }, [accountsData]);

  const handleAccountChange = (newAccountId: string) => {
    setSelectedAccountId(newAccountId);
    setPaginationParameters((prev) => ({ ...prev, page: 0 }));
    updateQueryParams({ accountId: newAccountId, page: 0 });
  };

  const handleQuery = (valueToSearch: string) => {
    setQuery(valueToSearch);
    setPaginationParameters((prev) => ({ ...prev, page: 0 }));
    updateQueryParams({ query: valueToSearch, page: 0 });
  };

  const handlePaginationChange = (
    value: { page: number; rowsPerPage: number } |
    ((prev: { page: number; rowsPerPage: number }) => { page: number; rowsPerPage: number }),
  ) => {
    const newPagination = typeof value === 'function' ? value(paginationParameters) : value;
    setPaginationParameters(newPagination);
    updateQueryParams({ page: newPagination.page, rowsPerPage: newPagination.rowsPerPage });
  };

  return (
    <Page
      className={classes.root}
      title='Campos'
    >
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} p={2}>
          <CustomHeader title='Campos' showBackButton={false} />
          <Box display="flex" alignItems="center" gap={2}>
            <FarmsCounter />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="account-filter-label">Cuenta</InputLabel>
              <Select
                labelId="account-filter-label"
                value={selectedAccountId}
                label="Cuenta"
                onChange={(e) => handleAccountChange(e.target.value)}
              >
                <MenuItem value="">Todas las cuentas</MenuItem>
                {accounts.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <FarmsTableContent
          selectedAccountId={selectedAccountId}
          query={query}
          handleQuery={handleQuery}
          paginationParameters={paginationParameters}
          setPaginationParameters={handlePaginationChange}
        />
      </Container>
    </Page>
  );
};

export default FarmsListView;
